<template>
  <div>
    <v-data-table
      :headers="computedHeaders"
      :items="customers"
      :options="tableOptions"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :footer-props="{
        itemsPerPageText: '行/ページ:',
        itemsPerPageOptions: [10, 25, 50],
      }"
      no-data-text="データがありません"
      loading-text="読込中..."
      class="customer-list-table"
      @click:row="onCustomerClick"
      @update:options="onOptionsUpdate"
    >
      <template #[`item.attributes.memberNumber`]="{ item }">
        {{ item.attributes.memberNumber || '-' }}
      </template>

      <template #[`item.attributes.name`]="{ item }">
        <div>
          <div>{{ item.attributes.name }}</div>
          <div
            v-if="item.attributes.customerTags && item.attributes.customerTags.length > 0"
            class="mt-1"
          >
            <v-chip
              v-for="tag in item.attributes.customerTags.slice(0, 3)"
              :key="tag.id"
              x-small
              outlined
              class="mr-1"
            >
              {{ tag.name }}
            </v-chip>
            <span
              v-if="item.attributes.customerTags.length > 3"
              class="text-caption grey--text"
            >
              +{{ item.attributes.customerTags.length - 3 }}
            </span>
          </div>
        </div>
      </template>

      <template #[`item.attributes.receiptAddressee`]="{ item }">
        {{ item.attributes.receiptAddressee || '-' }}
      </template>

      <template #[`item.attributes.visitCount`]="{ item }">
        {{ item.attributes.visitCount || 0 }}
      </template>

      <template #[`item.attributes.lastVisitedAt`]="{ item }">
        {{ formatDateTime(item.attributes.lastVisitedAt) }}
      </template>

      <template #[`item.attributes.birthday`]="{ item }">
        {{ formatBirthday(item.attributes.birthday) }}
      </template>

      <template #[`item.attributes.createdAt`]="{ item }">
        {{ formatDateTime(item.attributes.createdAt) }}
      </template>

      <template #[`item.attributes.casts`]="{ item }">
        <div v-if="item.attributes.casts && item.attributes.casts.length > 0">
          <v-chip
            v-for="cast in item.attributes.casts.slice(0, 2)"
            :key="cast.id"
            x-small
            outlined
            class="mr-1"
          >
            {{ cast.name }}
          </v-chip>
          <span
            v-if="item.attributes.casts.length > 2"
            class="text-caption grey--text"
          >
            +{{ item.attributes.casts.length - 2 }}
          </span>
        </div>
        <span v-else>-</span>
      </template>

      <!-- カスタムフィールド列 -->
      <template
        v-for="field in customFields"
        #[`item.customField_${field.id}`]="{ item }"
      >
        <span :key="field.id">{{ getCustomFieldValue(item, field) }}</span>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-menu
          offset-y
          left
        >
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              @click.stop
            >
              <v-icon small>
                {{ icons.mdiDotsVertical }}
              </v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click.stop="onCustomerClick(item)">
              <v-list-item-icon class="mr-2">
                <v-icon small>
                  {{ icons.mdiAccountDetails }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-title>詳細を見る</v-list-item-title>
            </v-list-item>
            <v-list-item @click.stop="onCreateTable(item)">
              <v-list-item-icon class="mr-2">
                <v-icon small>
                  {{ icons.mdiTablePlus }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-title>卓を作成</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import {
  mdiDotsVertical, mdiAccountDetails, mdiTablePlus,
} from '@mdi/js'
import { format } from 'date-fns'
import { formatDateTime } from '@/utils/formatters'

export default {
  name: 'CustomerList',
  props: {
    customers: {
      type: Array,
      default: () => [],
      required: true,
    },
    customFields: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 50,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const tableOptions = ref({
      page: props.page,
      itemsPerPage: props.perPage,
    })

    const serverItemsLength = computed(() => props.totalCount)

    const onOptionsUpdate = options => {
      tableOptions.value = options
      emit('update:pagination', {
        page: options.page,
        perPage: options.itemsPerPage,
      })
    }
    const baseHeaders = [
      {
        text: '会員番号',
        value: 'attributes.memberNumber',
        sortable: true,
        width: '100',
      },
      {
        text: '名前',
        value: 'attributes.name',
        sortable: true,
        width: '220',
      },
      {
        text: '領収書宛名',
        value: 'attributes.receiptAddressee',
        sortable: false,
        width: '150',
      },
      {
        text: '来店回数',
        value: 'attributes.visitCount',
        sortable: true,
        width: '100',
      },
      {
        text: '最終来店日',
        value: 'attributes.lastVisitedAt',
        sortable: true,
        width: '150',
      },
      {
        text: '誕生日',
        value: 'attributes.birthday',
        sortable: true,
        width: '100',
      },
      {
        text: '関連キャスト',
        value: 'attributes.casts',
        sortable: false,
        width: '150',
      },
      {
        text: '作成日時',
        value: 'attributes.createdAt',
        sortable: true,
        width: '150',
      },
    ]

    const actionsHeader = {
      text: '',
      value: 'actions',
      sortable: false,
      width: '50',
      align: 'end',
    }

    const computedHeaders = computed(() => {
      // カスタムフィールド列を生成
      const customFieldHeaders = props.customFields.map(field => ({
        text: field.name,
        value: `customField_${field.id}`,
        sortable: false,
        width: '120',
      }))

      return [...baseHeaders, ...customFieldHeaders, actionsHeader]
    })

    const formatBirthday = dateString => {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)

        return format(date, 'M/d')
      } catch (e) {
        return dateString
      }
    }

    const getCustomFieldValue = (customer, field) => {
      const customFieldValues = customer.attributes?.customFieldValues?.data || []
      const valueObj = customFieldValues.find(
        v => String(v.attributes?.customerCustomFieldId) === String(field.id),
      )

      if (!valueObj) return '-'

      const { value, typedValue } = valueObj.attributes

      // boolean型の場合
      if (field.type === 'boolean') {
        return typedValue === true ? 'はい' : 'いいえ'
      }

      return value || '-'
    }

    const onCustomerClick = customer => {
      emit('click', customer)
    }

    const onCreateTable = customer => {
      emit('create-table', customer)
    }

    return {
      tableOptions,
      serverItemsLength,
      computedHeaders,
      formatDateTime,
      formatBirthday,
      getCustomFieldValue,
      onCustomerClick,
      onCreateTable,
      onOptionsUpdate,
      icons: {
        mdiDotsVertical,
        mdiAccountDetails,
        mdiTablePlus,
      },
    }
  },
}
</script>

<style scoped>
/* 行をクリック可能にする */
::v-deep .v-data-table__wrapper tbody tr {
  cursor: pointer;
}

/* ホバー時のハイライト */
::v-deep .v-data-table__wrapper tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
